/**
 * @fileoverview Client-side PDF letterhead snapshot extraction
 *
 * Purpose:
 * - Render page 1 of a PDF at high resolution using pdfjs-dist
 * - Auto-detect header and footer regions via row-level whitespace analysis
 * - Allow user to tune gap tolerance, then crop and return as PNG
 *
 * Two-phase API:
 * 1. `renderPdfPage(file)` → renders once, returns reusable RenderedPage
 * 2. `cropLetterheadFromPage(page, gapFraction, trims?)` → fast re-crop at
 *    any tolerance, with optional per-zone trim offsets
 *
 * Dependencies:
 * - `utils/pdf/pdfjs` (Mozilla's PDF renderer, same engine as Firefox)
 */

import { pdfjsLib } from "@/utils/pdf/pdfjs";
import { PAGE_FIT_WIDTH_PX } from "./pageFit";
import type { SourceBodyMargins } from "./resolve-letterhead";

export type { SourceBodyMargins } from "./resolve-letterhead";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ExtractedLetterheadImages {
  headerImageFile: File | null;
  footerImageFile: File | null;
  leftSideImageFile: File | null;
  rightSideImageFile: File | null;
  /** Where each zone was cut, as fractions of the page (null = no zone). */
  selection: ZoneSelectionFractions;
  /**
   * The body column measured from the letter text between the header and
   * footer bands (px at 96dpi), or null for blank stationery — see
   * `detectSourceBodyMargins`.
   */
  sourceBodyMargins: SourceBodyMargins | null;
}

/** A zone's selection as fractions (0–1) of the page width / height. */
export interface SelectionFraction {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export type ZoneSelectionFractions = Record<CropZone, SelectionFraction | null>;

export interface CropOptions {
  /**
   * Cut the header tightly to its selection instead of page-wide. Used
   * when the header is saved as a side block that text flows beside — the
   * block's aspect ratio must be the artwork's own.
   */
  headerTight?: boolean;
}

/** Blob URLs for instant local preview (no upload needed) */
export interface LetterheadPreviewUrls {
  headerBlobUrl: string | null;
  footerBlobUrl: string | null;
  leftSideBlobUrl: string | null;
  rightSideBlobUrl: string | null;
}

/** Rendered page data that can be re-cropped without re-rendering the PDF */
export interface RenderedPage {
  canvas: HTMLCanvasElement;
  imageData: ImageData;
  contentRows: boolean[];
  /**
   * Column-level content classifier (true if at least one non-white pixel
   * exists in that column). Used by side-zone detection.
   */
  contentCols: boolean[];
  fileName: string;
}

/**
 * Per-zone trim offsets (in rendered pixels) applied after auto-detection.
 * Positive values shrink the band inward; negative values expand it outward
 * up to the page bounds.
 *   - `headerTrimPx`        — header trimmed from the bottom edge.
 *   - `headerTopTrimPx`     — header trimmed from the top edge (down to 0).
 *   - `headerLeftTrimPx`    — header trimmed from the left edge.
 *   - `headerRightTrimPx`   — header trimmed from the right edge.
 *   - `footerTrimPx`        — footer trimmed from the top edge.
 *   - `footerBottomTrimPx`  — footer trimmed from the bottom edge.
 *   - `footerLeftTrimPx`    — footer trimmed from the left edge.
 *   - `footerRightTrimPx`   — footer trimmed from the right edge.
 *   - `leftTrimPx`          — left side trimmed from the right (inner) edge.
 *   - `rightTrimPx`         — right side trimmed from the left (inner) edge.
 */
export interface CropTrims {
  headerTrimPx: number;
  headerTopTrimPx: number;
  headerLeftTrimPx: number;
  headerRightTrimPx: number;
  footerTrimPx: number;
  footerBottomTrimPx: number;
  footerLeftTrimPx: number;
  footerRightTrimPx: number;
  leftTrimPx: number;
  rightTrimPx: number;
  /**
   * Optional vertical trims for the side bands (rendered px from the page
   * top / bottom). The saved image keeps the full page height — rows
   * outside the range are painted white — so full-height side fit keeps
   * the band at the right place on the page instead of stretching it.
   */
  leftTopTrimPx?: number;
  leftBottomTrimPx?: number;
  rightTopTrimPx?: number;
  rightBottomTrimPx?: number;
}

export type CropZone = "header" | "footer" | "leftSide" | "rightSide";

/**
 * Which edge of a zone a trim controls. Header/footer support all four
 * edges; side bands have a single inner edge (the `edge` value is ignored
 * for them).
 */
export type TrimEdge = "top" | "bottom" | "left" | "right";

/** Map a (zone, edge) pair to its corresponding `CropTrims` field key. */
export function cropTrimKey(zone: CropZone, edge: TrimEdge): keyof CropTrims {
  if (zone === "header") {
    switch (edge) {
      case "top":
        return "headerTopTrimPx";
      case "left":
        return "headerLeftTrimPx";
      case "right":
        return "headerRightTrimPx";
      default:
        return "headerTrimPx"; // bottom
    }
  }
  if (zone === "footer") {
    switch (edge) {
      case "bottom":
        return "footerBottomTrimPx";
      case "left":
        return "footerLeftTrimPx";
      case "right":
        return "footerRightTrimPx";
      default:
        return "footerTrimPx"; // top
    }
  }
  if (zone === "leftSide") {
    if (edge === "top") return "leftTopTrimPx";
    if (edge === "bottom") return "leftBottomTrimPx";
    return "leftTrimPx";
  }
  if (edge === "top") return "rightTopTrimPx";
  if (edge === "bottom") return "rightBottomTrimPx";
  return "rightTrimPx";
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RENDER_SCALE = 3;

/**
 * Ceiling on the rendered canvas area. Browsers cap how large a canvas can
 * be — Safari/iOS refuses anything over ~16.7M pixels, and every browser
 * will fail `getContext`/`getImageData` once memory runs short (which is
 * what happens when several letterheads are processed back to back, since
 * each page costs ~35MB for the canvas plus another ~35MB for its
 * ImageData copy). A4/Letter at scale 3 sits at ~8.7M pixels, so this
 * budget only ever bites on unusually large pages, which get rendered at
 * a reduced scale instead of failing outright.
 */
const MAX_RENDER_PIXELS = 12_000_000;

/** Hard per-axis canvas limit enforced by browsers. */
const MAX_CANVAS_DIMENSION = 16_384;

const WHITE_THRESHOLD = 248;
const VERTICAL_PADDING = 90;
const HORIZONTAL_PADDING = 36;

/**
 * Regions thinner than this many rendered pixels are treated as decorative
 * elements (divider lines, rules) and merged into their nearest neighbor
 * so they don't cause the header to split at the divider.
 */
const THIN_REGION_THRESHOLD = 30;

/**
 * Minimum gap (in rendered pixels) between the side band and the next
 * content region for it to be treated as an independent side zone.
 */
const SIDE_GAP_MIN_PX = 60;

/**
 * Padding (in rendered pixels) added beyond the auto-detected content
 * edge of a side band, so the cropped image keeps a little breathing
 * room before the body starts.
 */
const SIDE_HORIZONTAL_PADDING = 30;

/**
 * Cap how far into the page a single side band is allowed to extend
 * by default — beyond this the auto-detect is considered noise rather
 * than a side band.
 */
const SIDE_MAX_FRACTION = 0.4;

/**
 * Blank-stationery detection. A blank letterhead is artwork at the top
 * and/or bottom with one large empty stretch where the letter goes. When the
 * biggest vertical gap covers at least `BLANK_STATIONERY_GAP_FRACTION` of
 * the page, everything above it is the header and everything below it the
 * footer — however many sub-blocks each is made of (a logo, then a contact
 * block a few lines lower). The bounds keep a short COMPLETED letter, which
 * also has a big gap under its text, on the first-region / last-region rule.
 */
const BLANK_STATIONERY_GAP_FRACTION = 0.4;
const BLANK_STATIONERY_HEADER_MAX_FRACTION = 0.35;
const BLANK_STATIONERY_FOOTER_MIN_FRACTION = 0.65;

/** Default gap fraction — a reasonable middle ground */
export const DEFAULT_GAP_FRACTION = 0.02;
/** Cap per-zone trim at 80% of the rendered page height */
export const MAX_TRIM_FRACTION = 0.8;
/** Allow expanding the crop up to 50% of the rendered page height beyond auto-detect */
export const MAX_EXPAND_FRACTION = 0.5;

/**
 * Horizontal padding applied around detected header/footer column bounds
 * when cropping. Exposed so the drag-selection overlay can convert between
 * on-page edge positions and trim values with the exact same math.
 */
export const CROP_HORIZONTAL_PADDING = HORIZONTAL_PADDING;

// ---------------------------------------------------------------------------
// Phase 1: Render PDF page (slow, do once)
// ---------------------------------------------------------------------------

/**
 * Render page 1 of a PDF to an offscreen canvas. Returns a RenderedPage
 * that can be passed to `cropLetterheadFromPage` multiple times (fast).
 */
export async function renderPdfPage(file: File): Promise<RenderedPage> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let canvas: HTMLCanvasElement | null = null;
  try {
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({
      scale: fitRenderScale(page.getViewport({ scale: 1 })),
    });
    canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    await page.render({ canvas, viewport }).promise;
    // Drop the page's cached operator lists / fonts before we allocate the
    // ImageData copy, so the two peaks don't overlap.
    page.cleanup();

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error(
        `Browser refused a ${canvas.width}×${canvas.height} canvas for this page`,
      );
    }
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const contentRows = classifyRows(imageData, WHITE_THRESHOLD);
    const contentCols = classifyColumns(imageData, WHITE_THRESHOLD);

    return { canvas, imageData, contentRows, contentCols, fileName: file.name };
  } catch (err) {
    if (canvas) releaseCanvas(canvas);
    throw err;
  } finally {
    // Awaited so the worker (and the page data it holds) is gone before the
    // next letterhead is rendered, rather than piling up across uploads.
    await pdf.loadingTask.destroy();
  }
}

/**
 * Pick the largest render scale up to {@link RENDER_SCALE} that keeps the
 * canvas inside the browser's area and per-axis limits.
 */
function fitRenderScale(baseViewport: {
  width: number;
  height: number;
}): number {
  const { width, height } = baseViewport;
  if (width <= 0 || height <= 0) return RENDER_SCALE;

  const areaScale = Math.sqrt(MAX_RENDER_PIXELS / (width * height));
  const axisScale = MAX_CANVAS_DIMENSION / Math.max(width, height);
  // Never go below 1 — a page big enough to need that is unusable anyway,
  // and pdf.js will surface a clearer error than a silently blank canvas.
  return Math.max(1, Math.min(RENDER_SCALE, areaScale, axisScale));
}

/**
 * Free a rendered page's canvas memory. Browsers only reclaim canvas
 * backing stores on GC, which lags badly when several multi-megapixel
 * pages are processed in one session; zeroing the dimensions releases the
 * buffer immediately.
 */
export function releaseRenderedPage(page: RenderedPage): void {
  releaseCanvas(page.canvas);
}

function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

// ---------------------------------------------------------------------------
// Auto-detected crop geometry (shared by cropping + the drag-select overlay)
// ---------------------------------------------------------------------------

/**
 * The untrimmed geometry auto-detection produces for a rendered page, in
 * rendered-canvas pixels. Trims are deltas against these values, so the
 * drag-selection overlay uses this to convert between absolute on-page
 * edge positions and the `CropTrims` the crop pipeline consumes.
 */
export interface AutoCropGeometry {
  canvasWidth: number;
  canvasHeight: number;
  /** Untrimmed header band (its top is always 0) — null when none detected. */
  header: { bottomPx: number; colLeftPx: number; colRightPx: number } | null;
  /** Untrimmed footer band (its bottom is the page bottom) — null when none detected. */
  footer: { topPx: number; colLeftPx: number; colRightPx: number } | null;
  /** Inner (right) edge of the detected left side band — null when none detected. */
  leftBandEndPx: number | null;
  /** Inner (left) edge of the detected right side band — null when none detected. */
  rightBandStartPx: number | null;
}

/**
 * Run the row/column auto-detection for a page WITHOUT cropping anything.
 * `cropLetterheadFromPage` derives its bands from this same geometry, so
 * rect math done against it (the drag overlay) matches the produced crops.
 */
export function computeAutoCropGeometry(
  page: RenderedPage,
  gapFraction: number,
): AutoCropGeometry {
  const minGap = Math.max(10, Math.round(page.canvas.height * gapFraction));
  const rawRegions = findContentRegions(page.contentRows, minGap);
  const regions = mergeDecorativeRegions(rawRegions, THIN_REGION_THRESHOLD);

  const headerFromRegion = (region: ContentRegion) => {
    const bottomPx = region.end + VERTICAL_PADDING;
    const colBounds = findContentColumnBounds(
      page.imageData,
      0,
      Math.max(1, bottomPx),
      WHITE_THRESHOLD,
    );
    return {
      bottomPx,
      colLeftPx: colBounds.left,
      colRightPx: colBounds.right,
    };
  };
  const footerFromRegion = (region: ContentRegion) => {
    const topPx = region.start - VERTICAL_PADDING;
    const colBounds = findContentColumnBounds(
      page.imageData,
      Math.max(0, topPx),
      page.canvas.height,
      WHITE_THRESHOLD,
    );
    return {
      topPx,
      colLeftPx: colBounds.left,
      colRightPx: colBounds.right,
    };
  };

  const { header, footer }: Pick<AutoCropGeometry, "header" | "footer"> =
    (() => {
      const blank = splitBlankStationery(regions, page.canvas.height);
      if (blank) {
        return {
          header: blank.header ? headerFromRegion(blank.header) : null,
          footer: blank.footer ? footerFromRegion(blank.footer) : null,
        };
      }
      if (regions.length >= 2) {
        return {
          header: headerFromRegion(regions[0]),
          footer: footerFromRegion(regions[regions.length - 1]),
        };
      }
      if (regions.length === 1) {
        const region = regions[0];
        const midpoint = page.canvas.height / 2;
        if (region.end < midpoint) {
          return { header: headerFromRegion(region), footer: null };
        }
        if (region.start > midpoint) {
          return { header: null, footer: footerFromRegion(region) };
        }
        return { header: headerFromRegion(region), footer: null };
      }
      return { header: null, footer: null };
    })();

  const minColGap = Math.max(10, Math.round(page.canvas.width * gapFraction));
  const colRegions = findContentRegions(page.contentCols, minColGap);
  const sideBands = detectSideBands(colRegions, page.canvas.width);

  return {
    canvasWidth: page.canvas.width,
    canvasHeight: page.canvas.height,
    header,
    footer,
    leftBandEndPx: sideBands.left?.end ?? null,
    rightBandStartPx: sideBands.right?.start ?? null,
  };
}

/** One zone's current crop rectangle, in rendered-canvas pixels. */
export interface ZoneSelectionRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Header / footer regions for BLANK stationery, or `null` when the page
 * doesn't look blank (see `BLANK_STATIONERY_GAP_FRACTION`) and the caller
 * should fall back to the first-region / last-region rule.
 *
 * The page's largest empty stretch — between two regions, above the first,
 * or below the last — is where the letter goes. Everything above it merges
 * into one header region and everything below into one footer region, so a
 * contact block sitting a few lines under the logo stays in the header.
 */
export function splitBlankStationery(
  regions: ContentRegion[],
  pageHeight: number,
): { header: ContentRegion | null; footer: ContentRegion | null } | null {
  if (regions.length === 0 || pageHeight <= 0) return null;
  // Gap i sits before region i; the last gap is below the final region.
  const gaps = [
    regions[0].start,
    ...regions.slice(1).map((r, i) => r.start - regions[i].end),
    pageHeight - 1 - regions[regions.length - 1].end,
  ];
  const widest = gaps.reduce((best, g, i) => (g > gaps[best] ? i : best), 0);
  if (gaps[widest] < pageHeight * BLANK_STATIONERY_GAP_FRACTION) return null;

  const above = regions.slice(0, widest);
  const below = regions.slice(widest);
  const header =
    above.length > 0
      ? { start: above[0].start, end: above[above.length - 1].end }
      : null;
  const footer =
    below.length > 0
      ? { start: below[0].start, end: below[below.length - 1].end }
      : null;
  if (header && header.end > pageHeight * BLANK_STATIONERY_HEADER_MAX_FRACTION)
    return null;
  if (
    footer &&
    footer.start < pageHeight * BLANK_STATIONERY_FOOTER_MIN_FRACTION
  )
    return null;
  return { header, footer };
}

/**
 * Trimmed column bounds for a header/footer band, mirroring the fallback
 * inside the crop path: a trim that would collapse the band below 1px is
 * ignored entirely.
 */
function trimmedColBounds(
  colLeftPx: number,
  colRightPx: number,
  leftTrim: number,
  rightTrim: number,
): { left: number; right: number } {
  const left = colLeftPx + leftTrim;
  const right = colRightPx - rightTrim;
  return right - left < 1
    ? { left: colLeftPx, right: colRightPx }
    : { left, right };
}

/**
 * The crop rectangles a given set of trims produces, in rendered-canvas
 * pixels — exactly the bands `cropLetterheadFromPage` cuts. Side zones are
 * `null` when the page has no band there (none detected and no expansion).
 */
export function selectionRectsForTrims(
  geo: AutoCropGeometry,
  trims: CropTrims,
): {
  header: ZoneSelectionRect | null;
  footer: ZoneSelectionRect | null;
  leftSide: ZoneSelectionRect | null;
  rightSide: ZoneSelectionRect | null;
} {
  const W = geo.canvasWidth;
  const H = geo.canvasHeight;

  const buildHeaderRect = (
    headerGeo: NonNullable<AutoCropGeometry["header"]>,
  ): ZoneSelectionRect => {
    const top = Math.min(H, Math.max(0, trims.headerTopTrimPx));
    const bottom = Math.min(
      H,
      Math.max(top + 1, headerGeo.bottomPx - trims.headerTrimPx),
    );
    const cb = trimmedColBounds(
      headerGeo.colLeftPx,
      headerGeo.colRightPx,
      trims.headerLeftTrimPx,
      trims.headerRightTrimPx,
    );
    return {
      top,
      bottom,
      left: Math.max(0, cb.left - HORIZONTAL_PADDING),
      right: Math.min(W, cb.right + HORIZONTAL_PADDING),
    };
  };
  const header: ZoneSelectionRect | null = geo.header
    ? buildHeaderRect(geo.header)
    : null;

  const buildFooterRect = (
    footerGeo: NonNullable<AutoCropGeometry["footer"]>,
  ): ZoneSelectionRect => {
    const top = Math.max(0, footerGeo.topPx + trims.footerTrimPx);
    const bottom = Math.min(
      H,
      Math.max(top + 1, H - Math.max(0, trims.footerBottomTrimPx)),
    );
    const cb = trimmedColBounds(
      footerGeo.colLeftPx,
      footerGeo.colRightPx,
      trims.footerLeftTrimPx,
      trims.footerRightTrimPx,
    );
    return {
      top: Math.min(H, top),
      bottom,
      left: Math.max(0, cb.left - HORIZONTAL_PADDING),
      right: Math.min(W, cb.right + HORIZONTAL_PADDING),
    };
  };
  const footer: ZoneSelectionRect | null = geo.footer
    ? buildFooterRect(geo.footer)
    : null;

  const sideRows = (
    topTrim: number | undefined,
    bottomTrim: number | undefined,
  ) => {
    const top = Math.min(H, Math.max(0, topTrim ?? 0));
    const bottom = Math.max(top + 1, H - Math.max(0, bottomTrim ?? 0));
    return { top, bottom: Math.min(H, bottom) };
  };
  const leftBandEnd = geo.leftBandEndPx ?? (trims.leftTrimPx < 0 ? 0 : null);
  const leftSide: ZoneSelectionRect | null =
    leftBandEnd === null
      ? null
      : {
          ...sideRows(trims.leftTopTrimPx, trims.leftBottomTrimPx),
          left: 0,
          right: Math.min(W, Math.max(1, leftBandEnd - trims.leftTrimPx)),
        };

  const rightBandStart =
    geo.rightBandStartPx ?? (trims.rightTrimPx < 0 ? W : null);
  const rightSide: ZoneSelectionRect | null =
    rightBandStart === null
      ? null
      : {
          ...sideRows(trims.rightTopTrimPx, trims.rightBottomTrimPx),
          left: Math.max(
            0,
            Math.min(W - 1, rightBandStart + trims.rightTrimPx),
          ),
          right: W,
        };

  return { header, footer, leftSide, rightSide };
}

/**
 * Invert `selectionRectsForTrims` for one dragged edge: the trim value that
 * puts that zone edge at `posPx` (rendered-canvas px). Returns 0 when the
 * zone has no auto geometry to measure against (undetected header/footer).
 */
export function trimForEdgePosition(
  geo: AutoCropGeometry,
  zone: "header" | "footer" | "leftSide" | "rightSide",
  edge: "top" | "bottom" | "left" | "right",
  posPx: number,
): number {
  const W = geo.canvasWidth;
  const H = geo.canvasHeight;
  switch (zone) {
    case "header": {
      if (!geo.header) return 0;
      if (edge === "top") return posPx;
      if (edge === "bottom") return geo.header.bottomPx - posPx;
      if (edge === "left")
        return posPx + HORIZONTAL_PADDING - geo.header.colLeftPx;
      return geo.header.colRightPx - (posPx - HORIZONTAL_PADDING);
    }
    case "footer": {
      if (!geo.footer) return 0;
      if (edge === "top") return posPx - geo.footer.topPx;
      if (edge === "bottom") return H - posPx;
      if (edge === "left")
        return posPx + HORIZONTAL_PADDING - geo.footer.colLeftPx;
      return geo.footer.colRightPx - (posPx - HORIZONTAL_PADDING);
    }
    case "leftSide":
      if (edge === "top") return posPx;
      if (edge === "bottom") return H - posPx;
      return (geo.leftBandEndPx ?? 0) - posPx;
    case "rightSide":
      if (edge === "top") return posPx;
      if (edge === "bottom") return H - posPx;
      return posPx - (geo.rightBandStartPx ?? W);
  }
}

// ---------------------------------------------------------------------------
// Phase 2: Crop regions (fast, call on every slider change)
// ---------------------------------------------------------------------------

/**
 * Crop header/footer from an already-rendered page.
 * Returns File objects ready for upload AND blob URLs for instant preview.
 *
 * @param trims - Optional per-zone trim offsets that shrink the crop band
 *   inward (header trimmed from bottom, footer trimmed from top).
 */
export async function cropLetterheadFromPage(
  page: RenderedPage,
  gapFraction: number,
  trims?: CropTrims,
  options: CropOptions = {},
): Promise<ExtractedLetterheadImages & LetterheadPreviewUrls> {
  const geo = computeAutoCropGeometry(page, gapFraction);

  const headerTrim = trims?.headerTrimPx ?? 0;
  const headerTopTrim = trims?.headerTopTrimPx ?? 0;
  const headerLeftTrim = trims?.headerLeftTrimPx ?? 0;
  const headerRightTrim = trims?.headerRightTrimPx ?? 0;
  const footerTrim = trims?.footerTrimPx ?? 0;
  const footerBottomTrim = trims?.footerBottomTrimPx ?? 0;
  const footerLeftTrim = trims?.footerLeftTrimPx ?? 0;
  const footerRightTrim = trims?.footerRightTrimPx ?? 0;
  const leftTrim = trims?.leftTrimPx ?? 0;
  const rightTrim = trims?.rightTrimPx ?? 0;

  /**
   * Shrink (or, with negative values, expand) a header/footer column band
   * inward from its left and right edges, keeping `left < right`.
   */
  const trimColBounds = (
    cb: { left: number; right: number },
    leftPx: number,
    rightPx: number,
  ): { left: number; right: number } => {
    const left = cb.left + leftPx;
    const right = cb.right - rightPx;
    return right - left < 1 ? cb : { left, right };
  };

  const buildHeaderImageFile = (
    headerGeo: NonNullable<AutoCropGeometry["header"]>,
  ): Promise<File | null> => {
    const headerTop = Math.max(0, headerTopTrim);
    const headerBottom = headerGeo.bottomPx - headerTrim;
    return cropRegionToFile(
      page.canvas,
      page.imageData,
      { top: headerTop, bottom: Math.max(headerTop + 1, headerBottom) },
      page.fileName,
      "header",
      trimColBounds(
        { left: headerGeo.colLeftPx, right: headerGeo.colRightPx },
        headerLeftTrim,
        headerRightTrim,
      ),
      !options.headerTight,
    );
  };
  const headerImageFile: File | null = geo.header
    ? await buildHeaderImageFile(geo.header)
    : null;

  const buildFooterImageFile = (
    footerGeo: NonNullable<AutoCropGeometry["footer"]>,
  ): Promise<File | null> => {
    const footerTopClamped = Math.max(0, footerGeo.topPx + footerTrim);
    const footerBottom = page.canvas.height - Math.max(0, footerBottomTrim);
    return cropRegionToFile(
      page.canvas,
      page.imageData,
      {
        top: footerTopClamped,
        bottom: Math.max(footerTopClamped + 1, footerBottom),
      },
      page.fileName,
      "footer",
      trimColBounds(
        { left: footerGeo.colLeftPx, right: footerGeo.colRightPx },
        footerLeftTrim,
        footerRightTrim,
      ),
    );
  };
  const footerImageFile: File | null = geo.footer
    ? await buildFooterImageFile(geo.footer)
    : null;

  // ---------------------------------------------------------------------
  // Side bands (optional) — auto-detected leftmost / rightmost contiguous
  // content columns separated from the rest of the page by a gap. When no
  // band was detected, a negative (expand) trim still creates one from the
  // page edge — that's how the drag-select overlay lets the user pull out
  // a side band the auto-detect missed.
  // ---------------------------------------------------------------------
  const leftBandEnd = geo.leftBandEndPx ?? (leftTrim < 0 ? 0 : null);
  const leftSideImageFile: File | null =
    leftBandEnd !== null
      ? await cropSideRegionToFile(
          page.canvas,
          {
            left: 0,
            right: Math.max(1, leftBandEnd - leftTrim),
          },
          page.fileName,
          "leftSide",
          sideRowRange(
            page.canvas.height,
            trims?.leftTopTrimPx,
            trims?.leftBottomTrimPx,
          ),
        )
      : null;

  const rightBandStart =
    geo.rightBandStartPx ?? (rightTrim < 0 ? page.canvas.width : null);
  const rightSideImageFile: File | null =
    rightBandStart !== null
      ? await cropSideRegionToFile(
          page.canvas,
          {
            left: Math.max(0, rightBandStart + rightTrim),
            right: page.canvas.width,
          },
          page.fileName,
          "rightSide",
          sideRowRange(
            page.canvas.height,
            trims?.rightTopTrimPx,
            trims?.rightBottomTrimPx,
          ),
        )
      : null;

  const zeroTrims: CropTrims = {
    headerTrimPx: 0,
    headerTopTrimPx: 0,
    headerLeftTrimPx: 0,
    headerRightTrimPx: 0,
    footerTrimPx: 0,
    footerBottomTrimPx: 0,
    footerLeftTrimPx: 0,
    footerRightTrimPx: 0,
    leftTrimPx: 0,
    rightTrimPx: 0,
  };
  const rects = selectionRectsForTrims(geo, trims ?? zeroTrims);
  const toFraction = (r: ZoneSelectionRect | null, has: boolean) =>
    r && has
      ? {
          top: r.top / page.canvas.height,
          bottom: r.bottom / page.canvas.height,
          left: r.left / page.canvas.width,
          right: r.right / page.canvas.width,
        }
      : null;
  const selection: ZoneSelectionFractions = {
    header: toFraction(rects.header, !!headerImageFile),
    footer: toFraction(rects.footer, !!footerImageFile),
    leftSide: toFraction(rects.leftSide, !!leftSideImageFile),
    rightSide: toFraction(rects.rightSide, !!rightSideImageFile),
  };
  const sourceBodyMargins = detectSourceBodyMargins(page.imageData, rects);

  const headerBlobUrl = headerImageFile
    ? URL.createObjectURL(headerImageFile)
    : null;
  const footerBlobUrl = footerImageFile
    ? URL.createObjectURL(footerImageFile)
    : null;
  const leftSideBlobUrl = leftSideImageFile
    ? URL.createObjectURL(leftSideImageFile)
    : null;
  const rightSideBlobUrl = rightSideImageFile
    ? URL.createObjectURL(rightSideImageFile)
    : null;

  return {
    headerImageFile,
    footerImageFile,
    leftSideImageFile,
    rightSideImageFile,
    headerBlobUrl,
    footerBlobUrl,
    leftSideBlobUrl,
    rightSideBlobUrl,
    selection,
    sourceBodyMargins,
  };
}

/** The pixel buffer of a rendered page — `ImageData`, or a plain stand-in. */
export interface ImageDataLike {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

/**
 * Measure the firm's body column from the SOURCE page (MOR-1432).
 *
 * A page-fit letterhead keeps its artwork where the firm put it, so the
 * body must start where the firm's letters start too — not at the app's
 * default margins. On a completed letter the text between the header and
 * footer bands is that column: its leftmost and rightmost ink give the
 * left and right body margins. The scan skips the header and footer bands
 * (and any side band's columns, so the margins are measured from the
 * band's inner edge, which is where the body starts beside it).
 *
 * Returns px at 96dpi on the A4 page every renderer lays out, or null when
 * there is no ink to measure (blank stationery): the caller then falls
 * back to the firm's margins.
 */
export function detectSourceBodyMargins(
  imageData: ImageDataLike,
  rects: {
    header: ZoneSelectionRect | null;
    footer: ZoneSelectionRect | null;
    leftSide: ZoneSelectionRect | null;
    rightSide: ZoneSelectionRect | null;
  },
): SourceBodyMargins | null {
  const { width, height, data } = imageData;
  if (width <= 0 || height <= 0) return null;
  const top = Math.max(0, Math.round(rects.header?.bottom ?? 0));
  const bottom = Math.min(height, Math.round(rects.footer?.top ?? height));
  const colStart = Math.max(0, Math.round(rects.leftSide?.right ?? 0));
  const colEnd = Math.min(width, Math.round(rects.rightSide?.left ?? width));
  if (bottom - top < 1 || colEnd - colStart < 1) return null;

  const ink = { left: colEnd, right: colStart };
  for (let y = top; y < bottom; y++) {
    const rowOffset = y * width * 4;
    for (let x = colStart; x < colEnd; x++) {
      const i = rowOffset + x * 4;
      if (
        data[i] < WHITE_THRESHOLD ||
        data[i + 1] < WHITE_THRESHOLD ||
        data[i + 2] < WHITE_THRESHOLD
      ) {
        if (x < ink.left) ink.left = x;
        if (x + 1 > ink.right) ink.right = x + 1;
      }
    }
  }
  if (ink.left >= ink.right) return null;

  const scale = PAGE_FIT_WIDTH_PX / width;
  return {
    left: Math.max(0, Math.round((ink.left - colStart) * scale)),
    right: Math.max(0, Math.round((colEnd - ink.right) * scale)),
  };
}

/**
 * Trims that reproduce a saved selection (page fractions) on this page's
 * auto-detected geometry — how the editor reopens where the user left it.
 */
export function trimsForSelection(
  geo: AutoCropGeometry,
  selection: Partial<Record<CropZone, SelectionFraction | null | undefined>>,
): Partial<CropTrims> {
  const W = geo.canvasWidth;
  const H = geo.canvasHeight;
  const patch: Partial<CropTrims> = {};
  const set = (zone: CropZone, edge: TrimEdge, pos: number) => {
    patch[cropTrimKey(zone, edge)] = Math.round(
      trimForEdgePosition(geo, zone, edge, pos),
    );
  };
  const h = selection.header;
  if (h && geo.header) {
    set("header", "top", h.top * H);
    set("header", "bottom", h.bottom * H);
    set("header", "left", h.left * W);
    set("header", "right", h.right * W);
  }
  const f = selection.footer;
  if (f && geo.footer) {
    set("footer", "top", f.top * H);
    set("footer", "bottom", f.bottom * H);
    set("footer", "left", f.left * W);
    set("footer", "right", f.right * W);
  }
  const l = selection.leftSide;
  if (l) {
    set("leftSide", "right", l.right * W);
    set("leftSide", "top", l.top * H);
    set("leftSide", "bottom", l.bottom * H);
  }
  const r = selection.rightSide;
  if (r) {
    set("rightSide", "left", r.left * W);
    set("rightSide", "top", r.top * H);
    set("rightSide", "bottom", r.bottom * H);
  }
  return patch;
}

/**
 * Pick a leftmost / rightmost side band from a list of column-level
 * content regions. A side band qualifies only when:
 *   1. It starts (or ends) within a fraction of the page edge, AND
 *   2. There's a noticeable gap between it and the next region inward.
 *
 * This keeps narrow body content (e.g. a single column of body text) from
 * being misidentified as a side band.
 */
function detectSideBands(
  colRegions: ContentRegion[],
  pageWidth: number,
): { left: ContentRegion | null; right: ContentRegion | null } {
  if (colRegions.length === 0) return { left: null, right: null };
  if (colRegions.length === 1) {
    // Single region = the body content; no side bands.
    return { left: null, right: null };
  }

  const maxBandWidth = Math.round(pageWidth * SIDE_MAX_FRACTION);
  const edgeTolerance = Math.round(pageWidth * 0.06);

  const first = colRegions[0];
  const second = colRegions[1];
  const last = colRegions[colRegions.length - 1];
  const secondLast = colRegions[colRegions.length - 2];

  const leftGap = second.start - first.end;
  const left =
    first.start <= edgeTolerance &&
    leftGap >= SIDE_GAP_MIN_PX &&
    first.end - first.start <= maxBandWidth
      ? {
          start: first.start,
          end: Math.min(pageWidth, first.end + SIDE_HORIZONTAL_PADDING),
        }
      : null;

  const rightGap = last.start - secondLast.end;
  const right =
    last.end >= pageWidth - edgeTolerance &&
    rightGap >= SIDE_GAP_MIN_PX &&
    last.end - last.start <= maxBandWidth
      ? {
          start: Math.max(0, last.start - SIDE_HORIZONTAL_PADDING),
          end: last.end,
        }
      : null;

  return { left, right };
}

interface ColumnBand {
  left: number;
  right: number;
}

/**
 * Crop a full-height vertical band from the source canvas. Side images
 * always span the entire page height (top to bottom); only the column
 * range is detected/trimmed. This mirrors how header/footer crop the
 * full page width but a detected row band.
 */
/** Vertical range of a side band kept from its top/bottom trims (or null = full height). */
function sideRowRange(
  height: number,
  topTrim: number | undefined,
  bottomTrim: number | undefined,
): { top: number; bottom: number } | null {
  const top = Math.min(height, Math.max(0, Math.round(topTrim ?? 0)));
  const bottom = Math.max(
    top + 1,
    height - Math.max(0, Math.round(bottomTrim ?? 0)),
  );
  if (top <= 0 && bottom >= height) return null;
  return { top, bottom: Math.min(height, bottom) };
}

async function cropSideRegionToFile(
  sourceCanvas: HTMLCanvasElement,
  band: ColumnBand,
  sourceFileName: string,
  zone: "leftSide" | "rightSide",
  rows: { top: number; bottom: number } | null = null,
): Promise<File | null> {
  const clampedLeft = Math.max(0, Math.round(band.left));
  const clampedRight = Math.min(sourceCanvas.width, Math.round(band.right));
  const bandWidth = clampedRight - clampedLeft;
  if (bandWidth <= 0) return null;

  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = bandWidth;
  cropCanvas.height = sourceCanvas.height;
  const ctx = cropCanvas.getContext("2d");
  if (!ctx) return null;

  // Keep the full page height so the band lands where it was on the page;
  // rows outside the selected range are left white.
  const top = rows?.top ?? 0;
  const bottom = rows?.bottom ?? sourceCanvas.height;
  if (rows) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, bandWidth, sourceCanvas.height);
  }
  ctx.drawImage(
    sourceCanvas,
    clampedLeft,
    top,
    bandWidth,
    bottom - top,
    0,
    top,
    bandWidth,
    bottom - top,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    cropCanvas.toBlob((b) => resolve(b), "image/png");
  });
  if (!blob) return null;

  return new File([blob], buildSideSnapshotFileName(sourceFileName, zone), {
    type: "image/png",
  });
}

function buildSideSnapshotFileName(
  sourceFileName: string,
  zone: "leftSide" | "rightSide",
): string {
  const baseName = sourceFileName.replace(/\.[^.]+$/, "");
  const kebab = zone === "leftSide" ? "left-side" : "right-side";
  return `${baseName}.${kebab}-letterhead.png`;
}

// ---------------------------------------------------------------------------
// Row / column analysis
// ---------------------------------------------------------------------------

function classifyRows(imageData: ImageData, threshold: number): boolean[] {
  const { width, height, data } = imageData;
  const result = new Array<boolean>(height).fill(false);

  for (let y = 0; y < height; y++) {
    const rowOffset = y * width * 4;
    for (let x = 0; x < width; x++) {
      const i = rowOffset + x * 4;
      if (
        data[i] < threshold ||
        data[i + 1] < threshold ||
        data[i + 2] < threshold
      ) {
        result[y] = true;
        break;
      }
    }
  }

  return result;
}

/**
 * Column-level analog of `classifyRows`: a column is "content" if at least
 * one pixel in that column is darker than the white threshold. Used by
 * side-band auto-detection.
 */
function classifyColumns(imageData: ImageData, threshold: number): boolean[] {
  const { width, height, data } = imageData;
  const result = new Array<boolean>(width).fill(false);

  for (let y = 0; y < height; y++) {
    const rowOffset = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (result[x]) continue;
      const i = rowOffset + x * 4;
      if (
        data[i] < threshold ||
        data[i + 1] < threshold ||
        data[i + 2] < threshold
      ) {
        result[x] = true;
      }
    }
  }

  return result;
}

function findContentColumnBounds(
  imageData: ImageData,
  startY: number,
  endY: number,
  threshold: number,
): { left: number; right: number } {
  const { width, data } = imageData;
  const clampedStart = Math.max(0, Math.round(startY));
  const clampedEnd = Math.min(imageData.height, Math.round(endY));

  let left = width;
  let right = 0;

  for (let y = clampedStart; y < clampedEnd; y++) {
    const rowOffset = y * width * 4;
    for (let x = 0; x < width; x++) {
      const i = rowOffset + x * 4;
      if (
        data[i] < threshold ||
        data[i + 1] < threshold ||
        data[i + 2] < threshold
      ) {
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }

  if (left > right) {
    return { left: 0, right: width };
  }

  return { left, right: right + 1 };
}

// ---------------------------------------------------------------------------
// Region detection
// ---------------------------------------------------------------------------

/** A run of content rows/columns, in rendered-canvas pixels (inclusive). */
export interface ContentRegion {
  start: number;
  end: number;
}

function findContentRegions(
  contentRows: boolean[],
  minGapRows: number,
): ContentRegion[] {
  const regions: ContentRegion[] = [];
  let regionStart: number | null = null;
  let gapCount = 0;

  for (let y = 0; y < contentRows.length; y++) {
    if (contentRows[y]) {
      if (regionStart === null) {
        regionStart = y;
      }
      gapCount = 0;
    } else if (regionStart !== null) {
      gapCount++;
      if (gapCount >= minGapRows) {
        regions.push({ start: regionStart, end: y - gapCount });
        regionStart = null;
        gapCount = 0;
      }
    }
  }

  if (regionStart !== null) {
    const trailingBlank = gapCount;
    regions.push({
      start: regionStart,
      end: contentRows.length - 1 - trailingBlank,
    });
  }

  return regions;
}

/**
 * Merge thin regions (like divider lines) into their nearest neighbor.
 * A region spanning fewer than `maxThickness` rows is absorbed by the
 * region immediately before or after it, whichever is closer.
 */
function mergeDecorativeRegions(
  regions: ContentRegion[],
  maxThickness: number,
): ContentRegion[] {
  if (regions.length < 2) return regions;

  const merged: ContentRegion[] = [];

  for (let i = 0; i < regions.length; i++) {
    const region = regions[i];
    const thickness = region.end - region.start;

    if (thickness < maxThickness && merged.length > 0) {
      const prev = merged[merged.length - 1];
      prev.end = region.end;
    } else if (thickness < maxThickness && i + 1 < regions.length) {
      const next = regions[i + 1];
      regions[i + 1] = { start: region.start, end: next.end };
    } else {
      merged.push({ ...region });
    }
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Canvas cropping
// ---------------------------------------------------------------------------

interface CropBand {
  top: number;
  bottom: number;
}

async function cropRegionToFile(
  sourceCanvas: HTMLCanvasElement,
  imageData: ImageData,
  band: CropBand,
  sourceFileName: string,
  zone: "header" | "footer",
  fixedColBounds?: { left: number; right: number },
  pageWide: boolean = true,
): Promise<File | null> {
  const clampedTop = Math.max(0, Math.round(band.top));
  const clampedBottom = Math.min(sourceCanvas.height, Math.round(band.bottom));
  const bandHeight = clampedBottom - clampedTop;
  if (bandHeight <= 0) return null;

  const colBounds =
    fixedColBounds ??
    findContentColumnBounds(
      imageData,
      clampedTop,
      clampedBottom,
      WHITE_THRESHOLD,
    );

  const cropLeft = Math.max(0, colBounds.left - HORIZONTAL_PADDING);
  const cropRight = Math.min(
    sourceCanvas.width,
    colBounds.right + HORIZONTAL_PADDING,
  );
  const cropWidth = cropRight - cropLeft;
  if (cropWidth <= 0) return null;

  // The saved image spans the FULL page width with everything outside the
  // selected columns painted white. Exports draw header/footer images at
  // the header column's width, so a page-wide image keeps the artwork at
  // its true on-page position and relative size; a tight crop would be
  // stretched to fill the column (a small right-hand logo block became a
  // huge centred one).
  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = pageWide ? sourceCanvas.width : cropWidth;
  cropCanvas.height = bandHeight;
  const ctx = cropCanvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, cropCanvas.width, bandHeight);
  ctx.drawImage(
    sourceCanvas,
    cropLeft,
    clampedTop,
    cropWidth,
    bandHeight,
    pageWide ? cropLeft : 0,
    0,
    cropWidth,
    bandHeight,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    cropCanvas.toBlob((b) => resolve(b), "image/png");
  });
  if (!blob) return null;

  return new File([blob], buildSnapshotFileName(sourceFileName, zone), {
    type: "image/png",
  });
}

function buildSnapshotFileName(
  sourceFileName: string,
  zone: "header" | "footer",
): string {
  const baseName = sourceFileName.replace(/\.[^.]+$/, "");
  return `${baseName}.${zone}-letterhead.png`;
}
